// La acción rápida de cada card de la columna de resultados, al lado del botón de
// verificar (ADR-2026-09-07-acciones-rapidas-en-la-card).
//
// Qué botón es depende de la función avanzada de pre-lanzamientos, con la MISMA regla que
// las cinco barras de acción del historial y del inline
// (ADR-2026-09-06-enviar-a-lanzamiento): con la función prendida, la acción primaria de
// una imagen es mandarla al lanzamiento; sin ella, descargarla. Los videos se descargan
// siempre: un lanzamiento sube fotos de producto.
//
// Look de pastilla translúcida, igual que el botón de verificar y el chip del modo: sobre
// la foto, todos los controles de la card se leen igual.

import React, { useMemo } from 'react';
import { AudioLines, Download, Loader2 } from 'lucide-react';
import EnviarALanzamiento, { type ImagenParaLanzamiento } from '../../../components/EnviarALanzamiento';
import type { DatosCelda } from './useDatosDeCeldas';

/** Mismas clases que `BotonVerificar`: los dos son la misma clase de control. */
export const CLASES_PASTILLA =
  'inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-colors hover:bg-black/60';

export interface AccionRapidaCreacionProps {
  /** Fila de la imagen; `undefined` mientras la query no volvió. */
  datos: DatosCelda | undefined;
  esVideo: boolean;
  companyId: string | null;
  brandId?: string | null;
  /** La empresa tiene prendida la función de pre-lanzamientos. */
  preLanzamientos: boolean;
  descargando: boolean;
  onDescargar: () => void;
  /** Sólo videos: abre "Corregir voz". Si no viene, la card queda como siempre
   *  (ADR-2026-09-21-corregir-voz-es-una-creacion-nueva-gratis). */
  onCorregirVoz?: () => void;
}

export const AccionRapidaCreacion: React.FC<AccionRapidaCreacionProps> = ({
  datos,
  esVideo,
  companyId,
  brandId,
  preLanzamientos,
  descargando,
  onDescargar,
  onCorregirVoz,
}) => {
  // Referencia estable: `EnviarALanzamiento` deriva de esta lista sus variantes y sus
  // callbacks, y la grilla puede tener decenas de cards re-renderizando.
  const imagenes = useMemo<ImagenParaLanzamiento[]>(
    () =>
      datos
        ? [
            {
              id: datos.id,
              fileName: datos.fileName,
              isVideo: esVideo,
              variantes_ids: datos.variantesIds,
              variante_id: datos.varianteId,
              datos_generacion: (datos.datosGeneracion ?? null) as Record<string, unknown> | null,
            },
          ]
        : [],
    [datos, esVideo],
  );

  // Sin la fila no se puede ni descargar (falta el `file_name`) ni resolver el producto:
  // no se dibuja nada, en vez de un botón que falla al tocarlo.
  if (!datos) return null;

  if (preLanzamientos && !esVideo) {
    return (
      <EnviarALanzamiento
        companyId={companyId}
        brandId={brandId}
        imagenes={imagenes}
        className={CLASES_PASTILLA}
        // El texto solo para lectores de pantalla y el tooltip: en una card de 265px
        // entra un ícono, no una etiqueta.
        labelClassName="sr-only"
        tamanoIcono="h-4 w-4"
        // El botón vive abajo a la derecha de la card: el panel se abre hacia arriba y
        // anclado a la derecha, o se saldría de la pantalla.
        direccion="arriba"
        alineacion="derecha"
      />
    );
  }

  const descargar = (
    <button
      type="button"
      aria-label="Descargar"
      title="Descargar"
      disabled={descargando || !datos.fileName}
      onClick={(e) => {
        e.stopPropagation();
        onDescargar();
      }}
      className={`${CLASES_PASTILLA} disabled:opacity-60`}
    >
      {descargando ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" />
      )}
    </button>
  );

  // Un video suma "Corregir voz" al lado de descargar: misma pastilla, el contenedor de la
  // card ya es una fila con gap.
  if (esVideo && onCorregirVoz) {
    return (
      <>
        <button
          type="button"
          aria-label="Corregir voz"
          title="Corregir voz"
          onClick={(e) => {
            e.stopPropagation();
            onCorregirVoz();
          }}
          className={CLASES_PASTILLA}
        >
          <AudioLines className="h-4 w-4" />
        </button>
        {descargar}
      </>
    );
  }
  return descargar;
};

export default AccionRapidaCreacion;
